import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { adminService } from '../../../services/adminService';
import { getCanonicalPricingTiers } from '../../../config/pricingTiers';
import { isRecognizedPlanKey, normalizePlanKey } from '../../../config/planFeatures';

const accountPlanValue = (account) => {
  const savedValue = String(account?.planType || account?.tier || '').trim();
  return savedValue && !isRecognizedPlanKey(savedValue)
    ? savedValue
    : normalizePlanKey(savedValue);
};

const AccountsTable = ({ accounts, onViewAccount, onImpersonate, onUpdateAccount }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [planChanged, setPlanChanged] = useState(false);
  const canonicalPricingTiers = getCanonicalPricingTiers(pricingTiers);

  useEffect(() => {
    adminService?.getAllPricingTiersAdmin?.()
      .then((tiers) => { if (tiers?.length) setPricingTiers(tiers); })
      .catch(() =>
        adminService?.getAllPricingTiers?.()
          .then((tiers) => { if (tiers?.length) setPricingTiers(tiers); })
          .catch(() => {})
      );
  }, []);

  const getPlanBadgeColor = (plan) => {
    const colors = {
      free: 'bg-gray-100 text-gray-800',
      starter: 'bg-sky-100 text-sky-800',
      professional: 'bg-blue-100 text-blue-800',
      enterprise: 'bg-purple-100 text-purple-800',
    };
    return colors?.[normalizePlanKey(plan)] || colors?.free;
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800'
    };
    return colors?.[status?.toLowerCase()] || colors?.active;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateLong = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleToggleExpand = (account) => {
    if (expandedId === account?.id) {
      setExpandedId(null);
      setEditData({});
      setSaveSuccess(null);
    } else {
      setExpandedId(account?.id);
      setEditData({
        planType: accountPlanValue(account),
        subscriptionStatus: account?.subscriptionStatus || 'active',
        companyName: account?.companyName || '',
      });
      setPlanChanged(false);
      setSaveSuccess(null);
    }
  };

  const handleSave = async (accountId) => {
    if (!onUpdateAccount) return;
    setSaving(true);
    setSaveSuccess(null);
    setSaveError(null);
    try {
      const updates = { ...editData };
      if (!planChanged) delete updates.planType;
      const updated = await onUpdateAccount(accountId, updates);
      // Sync editData with the saved values so the row reflects the new state
      if (updated) {
        setEditData({
          planType: accountPlanValue(updated) || editData?.planType,
          subscriptionStatus: updated?.subscriptionStatus || editData?.subscriptionStatus,
          companyName: updated?.companyName || editData?.companyName,
        });
        setPlanChanged(false);
      }
      setSaveSuccess(accountId);
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving account:', error);
      setSaveError(error?.message || 'Failed to save changes. Please try again.');
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (accounts?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No accounts found</h3>
        <p className="text-muted-foreground">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[34%]" />
          <col className="w-[12%]" />
          <col className="w-[12%]" />
          <col className="w-[7%]" />
          <col className="w-[10%]" />
          <col className="w-[13%]" />
          <col className="w-[12%]" />
        </colgroup>
        <thead className="bg-muted/30 border-b border-border">
          <tr>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Account
            </th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Plan
            </th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Status
            </th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Forms
            </th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Subs
            </th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Joined
            </th>
            <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {accounts?.map((account) => {
            const isExpanded = expandedId === account?.id;
            const planValue = accountPlanValue(account);
            const planKey = normalizePlanKey(planValue);
            const planLabel = isRecognizedPlanKey(planValue)
              ? canonicalPricingTiers.find((tier) => tier.planKey === planKey)?.tierName || 'Free'
              : `Unsupported: ${planValue}`;
            const editPlanOptions = [
              ...(!isRecognizedPlanKey(editData?.planType)
                ? [{ value: editData.planType, label: `Unsupported saved value: ${editData.planType}` }]
                : []),
              ...canonicalPricingTiers.map((tier) => ({ value: tier.planKey, label: tier.tierName })),
            ];
            return (
              <React.Fragment key={account?.id}>
                <tr className={`hover:bg-muted/20 transition-smooth ${isExpanded ? 'bg-muted/10' : ''}`}>
                  <td className="px-3 py-3 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="User" size={14} className="text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-foreground text-sm truncate">
                          {account?.fullName || account?.email || account?.tier || 'User'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {account?.companyName || account?.email || 'No company'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(account?.planType)}`}>
                       {planLabel}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(account?.subscriptionStatus)}`}>
                      {account?.subscriptionStatus || 'Active'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    {account?.totalForms || 0}
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    {account?.totalSubmissions || 0}
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">
                    {formatDate(account?.createdAt)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {onImpersonate && (
                        <button
                          onClick={() => onImpersonate(account)}
                          title="Login as this user"
                          className="p-1.5 rounded-md text-muted-foreground hover:text-amber-600 hover:bg-amber-50 transition-smooth"
                        >
                          <Icon name="LogIn" size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => onViewAccount?.(account)}
                        title="Open account details"
                        className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-smooth"
                      >
                        <Icon name="ExternalLink" size={14} />
                      </button>
                      <button
                        onClick={() => handleToggleExpand(account)}
                        title={isExpanded ? 'Collapse' : 'Quick edit'}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-smooth"
                      >
                        <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                  {/* Inline Accordion / Dropdown Details Row */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="px-0 py-0 border-t border-primary/20">
                        <div className="bg-muted/10 border-l-4 border-primary/40 px-6 py-5">
                          {/* Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h4 className="text-base font-semibold text-foreground">
                                Account Details — {account?.tier || 'User'}
                              </h4>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                ID: <span className="font-mono">{account?.id}</span>
                              </p>
                            </div>
                            {saveSuccess === account?.id && (
                              <div className="flex items-center gap-1.5 text-green-600 text-sm font-medium">
                                <Icon name="CheckCircle" size={16} />
                                Saved successfully
                              </div>
                            )}
                            {saveError && expandedId === account?.id && (
                              <div className="flex items-center gap-1.5 text-red-600 text-sm font-medium">
                                <Icon name="AlertCircle" size={16} />
                                {saveError}
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Company Name */}
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                                Company Name
                              </label>
                              <Input
                                value={editData?.companyName || ''}
                                onChange={(e) => setEditData({ ...editData, companyName: e?.target?.value })}
                                placeholder="Enter company name"
                                className="text-sm"
                              />
                            </div>

                            {/* Plan Type */}
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                                Plan Type
                              </label>
                              <Select
                                value={editData?.planType || 'free'}
                               onChange={(planType) => {
                                 setEditData({ ...editData, planType });
                                 setPlanChanged(true);
                               }}
                               options={editPlanOptions}
                              />
                            </div>

                            {/* Subscription Status */}
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                                Subscription Status
                              </label>
                              <Select
                                value={editData?.subscriptionStatus || 'active'}
                                onChange={(subscriptionStatus) => setEditData({ ...editData, subscriptionStatus })}
                                options={[
                                  { value: 'active', label: 'Active' },
                                  { value: 'inactive', label: 'Inactive' },
                                  { value: 'suspended', label: 'Suspended' }
                                ]}
                              />
                            </div>
                          </div>

                          {/* Read-only info row */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border/50">
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Created</p>
                              <p className="text-sm text-foreground">{formatDateLong(account?.createdAt)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Last Login</p>
                              <p className="text-sm text-foreground">{account?.lastLoginAt ? formatDateLong(account?.lastLoginAt) : 'Never'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total Forms</p>
                              <p className="text-sm font-semibold text-foreground">{account?.totalForms || 0}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total Submissions</p>
                              <p className="text-sm font-semibold text-foreground">{account?.totalSubmissions || 0}</p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border/50">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleSave(account?.id)}
                              loading={saving}
                              disabled={saving}
                            >
                              <Icon name="Save" size={15} className="mr-1.5" />
                              Save Changes
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleExpand(account)}
                            >
                              Close
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
    </div>
  );
};

export default AccountsTable;